import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTutorLessonResume,
  listLessons,
  postTutorProgressBatch,
  type LessonItem,
  type TutorLessonProgressDTO,
} from "../../modules/tutor/api/tutor.client";
import { ROUTES } from "../../utils/constants";
import { Button } from "../ui/Button";
import { ChevronDown, ChevronRight, Loader2, Play } from "lucide-react";

/** Match app chrome (header / Communication Can-Do): indigo → purple → pink. */
const CHROME_HERO =
  "bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 shadow-lg shadow-purple-500/25 border border-white/15";
const PROGRESS_FILL =
  "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-400";

function moduleLabel(lesson: LessonItem): string {
  const sk = lesson.section_key;
  if (sk != null && String(sk).trim()) return String(sk).trim();
  const tt = lesson.topic_ta;
  if (tt != null && String(tt).trim()) return String(tt).trim();
  return "Lessons";
}

function activityHint(lesson: LessonItem): string {
  const t = (lesson.lesson_type || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (t === "body_parts") return "உடல் உறுப்புகள்";
  if (t === "vocabulary" || t === "vocab") return "சொல்வங்கி";
  if (t === "reading") return "படிப்போம்";
  if (t === "rhyme" || t === "song") return "பாடல்";
  if (t === "story") return "கதை";
  const first = lesson.sublessons?.[0];
  if (first?.type) return String(first.type);
  return "AI Tutor";
}

type SubtopicRow = { no: string; title: string; type?: string; pct: number };

function buildSubtopicRows(lesson: LessonItem, prog?: TutorLessonProgressDTO): SubtopicRow[] {
  const api = prog?.sublessons;
  const fromLesson = lesson.sublessons || [];
  if (api?.length) {
    return api.map((s) => ({
      no: s.sublesson_no,
      title: (s.title || s.sublesson_no).trim(),
      type: s.type,
      pct: Math.min(100, Math.max(0, s.percent)),
    }));
  }
  if (fromLesson.length) {
    return fromLesson.map((s) => ({
      no: s.sublesson_no,
      title: (s.title || s.sublesson_no).trim(),
      type: s.type,
      pct: 0,
    }));
  }
  return [];
}

export const TutorLessonProgressPanel: React.FC = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<LessonItem[]>([]);
  const [progressByLesson, setProgressByLesson] = useState<
    Record<number, TutorLessonProgressDTO | undefined>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLessonNo, setExpandedLessonNo] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listLessons();
      setLessons(list);
      const slice = list.slice(0, 36);
      const nos = slice.map((l) => l.lesson_no);
      let batch: TutorLessonProgressDTO[] = [];
      if (nos.length > 0) {
        try {
          batch = await postTutorProgressBatch(nos);
        } catch {
          batch = [];
        }
      }
      const map: Record<number, TutorLessonProgressDTO | undefined> = {};
      for (const p of batch) {
        if (p?.lesson_no != null) map[p.lesson_no] = p;
      }
      setProgressByLesson(map);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load lesson progress.");
      setLessons([]);
      setProgressByLesson({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sections = useMemo(() => {
    const m = new Map<string, LessonItem[]>();
    for (const l of lessons) {
      const key = moduleLabel(l);
      const arr = m.get(key) || [];
      arr.push(l);
      m.set(key, arr);
    }
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mod, items]) => ({
        mod,
        items: [...items].sort((a, b) => a.lesson_no - b.lesson_no),
      }));
  }, [lessons]);

  const primaryLesson = useMemo(() => {
    if (!lessons.length) return null;
    const scored = lessons.map((l) => ({
      l,
      p: progressByLesson[l.lesson_no]?.lesson_percent ?? 0,
    }));
    const inProgress = scored.find((x) => x.p > 0 && x.p < 100);
    if (inProgress) return inProgress.l;
    const notStarted = scored.find((x) => x.p === 0);
    if (notStarted) return notStarted.l;
    return lessons[0];
  }, [lessons, progressByLesson]);

  const nextLessonHint = useMemo(() => {
    if (!primaryLesson || lessons.length < 2) return null;
    const idx = lessons.findIndex((l) => l.lesson_no === primaryLesson.lesson_no);
    if (idx < 0 || idx >= lessons.length - 1) return null;
    const n = lessons[idx + 1];
    return { mod: moduleLabel(n), title: n.lesson_title, no: n.lesson_no };
  }, [lessons, primaryLesson]);

  const primaryPct = primaryLesson
    ? Math.min(100, Math.max(0, progressByLesson[primaryLesson.lesson_no]?.lesson_percent ?? 0))
    : 0;

  function resumeUrl(lessonNo: number, sublessonNo: string, chunkIdx: number): string {
    const base = ROUTES.STUDENT_TAMIL_TUTOR;
    const q = new URLSearchParams();
    q.set("lesson", String(lessonNo));
    q.set("sublesson", sublessonNo);
    q.set("chunk", String(chunkIdx));
    return `${base}?${q.toString()}`;
  }

  async function goResumeLesson(lessonNo: number): Promise<void> {
    try {
      const r = await getTutorLessonResume(lessonNo);
      navigate(resumeUrl(r.lesson_no, r.sublesson_no, r.chunk_order_index));
    } catch {
      const lesson = lessons.find((l) => l.lesson_no === lessonNo);
      const first = lesson?.sublessons?.[0]?.sublesson_no;
      if (first) navigate(resumeUrl(lessonNo, first, 0));
    }
  }

  async function goResumeSublesson(lessonNo: number, sublessonNo: string): Promise<void> {
    try {
      const r = await getTutorLessonResume(lessonNo);
      if (r.sublesson_no === sublessonNo) {
        navigate(resumeUrl(r.lesson_no, r.sublesson_no, r.chunk_order_index));
        return;
      }
    } catch {
      /* fall through */
    }
    navigate(resumeUrl(lessonNo, sublessonNo, 0));
  }

  function toggleLesson(lessonNo: number): void {
    setExpandedLessonNo((prev) => (prev === lessonNo ? null : lessonNo));
  }

  if (loading) {
    return (
      <div className="mt-6 rounded-2xl border border-app-border dark:border-slate-700 bg-app-surface dark:bg-slate-900/50 p-6 flex items-center gap-2 text-app-text-muted dark:text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin shrink-0 text-purple-600 dark:text-pink-400" />
        <span className="text-sm">Loading AI Tutor progress…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 rounded-2xl border border-warning-200 dark:border-warning-800 bg-warning-50/80 dark:bg-warning-950/30 px-4 py-3 text-sm text-warning-900 dark:text-warning-100">
        {error}
      </div>
    );
  }

  if (lessons.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 space-y-4">
      {primaryLesson && (
        <div className={`rounded-2xl overflow-hidden text-white ${CHROME_HERO}`}>
          <div className="px-4 sm:px-5 py-3 border-b border-white/20">
            <p className="text-[11px] sm:text-xs uppercase tracking-wide text-white/90">இன்றைய கற்றல்</p>
            <p className="text-sm font-semibold">Today&apos;s Learning</p>
          </div>
          <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-[11px] text-white/85">தொடரவும் · Resume</p>
              <h3 className="text-lg sm:text-xl font-bold truncate">AI Tutor</h3>
              <p className="text-sm text-white/95 truncate">
                <span className="font-medium">{moduleLabel(primaryLesson)}</span>
                <span className="mx-1.5 opacity-80">→</span>
                <span>{primaryLesson.lesson_title}</span>
              </p>
              <div className="space-y-1 pt-1">
                <div className="flex justify-between text-[11px] text-white/90">
                  <span>முன்னேற்றம்</span>
                  <span className="font-semibold tabular-nums">{primaryPct.toFixed(0)}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-black/20 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-white/95 dark:bg-white/90 transition-all duration-300"
                    style={{ width: `${primaryPct}%` }}
                  />
                </div>
              </div>
              {nextLessonHint && (
                <p className="text-[11px] text-white/85 pt-1 truncate">
                  அடுத்து: {nextLessonHint.mod} → {nextLessonHint.title}
                </p>
              )}
            </div>
            <Button
              type="button"
              aria-label="Resume AI Tutor"
              className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 rounded-xl !bg-white !text-purple-700 hover:!bg-indigo-50 shadow-lg border-0 p-0 focus:ring-white/50"
              onClick={() => void goResumeLesson(primaryLesson.lesson_no)}
            >
              <Play className="h-7 w-7 ml-1" fill="currentColor" />
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-app-border dark:border-slate-700 bg-app-surface dark:bg-slate-950 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-app-border dark:border-slate-800 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-app-text dark:text-white leading-tight">
              AI ஆசிரியர் பாட முன்னேற்றம்
            </h2>
            <p className="text-xs text-app-text-muted dark:text-slate-400 mt-0.5">
              Tap a lesson to see subtopics and % — use Continue to open the tutor
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="text-xs font-medium text-purple-600 dark:text-pink-400 hover:underline shrink-0"
          >
            Refresh
          </button>
        </div>

        <div
          className="max-h-[min(20rem,45vh)] overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {sections.map(({ mod, items }) => (
            <div key={mod}>
              <div className="sticky top-0 z-[1] px-4 py-2 bg-app-surface-muted/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-app-border dark:border-slate-800 text-xs font-semibold text-app-text-muted dark:text-slate-300">
                {mod}
              </div>
              <ul className="divide-y divide-app-border dark:divide-slate-800">
                {items.map((lesson) => {
                  const pct = Math.min(
                    100,
                    Math.max(0, progressByLesson[lesson.lesson_no]?.lesson_percent ?? 0)
                  );
                  const prog = progressByLesson[lesson.lesson_no];
                  const subtopics = buildSubtopicRows(lesson, prog);
                  const open = expandedLessonNo === lesson.lesson_no;

                  return (
                    <li key={lesson.lesson_no}>
                      <div>
                        <button
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-app-surface-muted/60 dark:hover:bg-slate-900/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset"
                          onClick={() => toggleLesson(lesson.lesson_no)}
                          aria-expanded={open}
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 text-purple-600 dark:text-fuchsia-400 shrink-0">
                              {open ? (
                                <ChevronDown className="h-4 w-4" aria-hidden />
                              ) : (
                                <ChevronRight className="h-4 w-4" aria-hidden />
                              )}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="font-medium text-app-text dark:text-white text-sm leading-snug line-clamp-2">
                                    {lesson.lesson_title}
                                  </p>
                                  <p className="text-xs text-purple-600 dark:text-fuchsia-400 mt-0.5">
                                    {activityHint(lesson)}
                                  </p>
                                </div>
                                <span className="text-sm font-semibold tabular-nums text-app-text dark:text-slate-200 shrink-0">
                                  {pct.toFixed(0)}%
                                </span>
                              </div>
                              <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden ring-1 ring-inset ring-purple-500/10 dark:ring-purple-400/15">
                                <div
                                  className={`h-full rounded-full transition-all ${PROGRESS_FILL}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </button>

                        {open && (
                          <div className="px-4 pb-3 pl-11 space-y-2 border-t border-app-border/60 dark:border-slate-800/80 bg-app-surface-muted/40 dark:bg-slate-900/40">
                            {subtopics.length === 0 ? (
                              <p className="text-xs text-app-text-muted dark:text-slate-500 py-2">
                                No subtopics for this lesson.
                              </p>
                            ) : (
                              subtopics.map((sub) => (
                                <div
                                  key={sub.no}
                                  className="rounded-lg border border-app-border dark:border-slate-700 bg-app-surface dark:bg-slate-950/80 p-3"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-app-text dark:text-white line-clamp-2">
                                        {sub.title}
                                      </p>
                                      {sub.type ? (
                                        <p className="text-[11px] text-purple-600 dark:text-fuchsia-400 mt-0.5">
                                          {sub.type}
                                        </p>
                                      ) : null}
                                    </div>
                                    <span className="text-xs font-semibold tabular-nums text-app-text dark:text-slate-200 shrink-0">
                                      {sub.pct.toFixed(0)}%
                                    </span>
                                  </div>
                                  <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-primary-400 dark:bg-primary-500"
                                      style={{ width: `${sub.pct}%` }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="mt-2.5 w-full sm:w-auto text-xs font-semibold rounded-lg px-3 py-1.5 text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 dark:focus:ring-offset-slate-900 shadow-sm shadow-purple-500/20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void goResumeSublesson(lesson.lesson_no, sub.no);
                                    }}
                                  >
                                    Continue
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <p className="px-4 py-2 text-[10px] text-app-text-muted dark:text-slate-500 border-t border-app-border dark:border-slate-800">
          Lesson % is the average of subtopic %. Subtopic % counts finished segments only. Scroll inside this box for
          more lessons.
        </p>
      </div>
    </div>
  );
};
